
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Upload, File, Trash2 } from "lucide-react";
import { uploadFile, deleteFile } from "@/services/fileStorageService";
import { toast } from "@/hooks/use-toast";

interface FileUploadFormProps {
  lessonId: string;
  onFileUploaded: (filePath: string, fileName: string) => void;
  currentFilePath: string | null;
}

const FileUploadForm: React.FC<FileUploadFormProps> = ({ 
  lessonId,
  onFileUploaded,
  currentFilePath
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  
  // Extract current filename from path if it exists
  const currentFileName = currentFilePath ? 
    currentFilePath.split('/').pop() : 
    null;
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };
  
  const handleUpload = async () => {
    if (!file) {
      toast({
        title: "No file selected",
        description: "Please select a file to upload",
        variant: "destructive",
      });
      return;
    }
    
    try {
      setIsUploading(true);
      setProgress(0);
      
      // If there's already a file, delete it first
      if (currentFilePath) {
        await deleteFile('course-content', currentFilePath);
      }
      
      // Create folder path based on lesson ID - this will be used to organize files
      const folderPath = `lesson-${lessonId}`;
      
      // Upload the new file
      const filePath = await uploadFile('course-content', folderPath, file);
      
      // Progress simulation for better UX
      const interval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 95) {
            clearInterval(interval);
            return 95;
          }
          return prev + 5;
        });
      }, 50);
      
      // Notify parent component about the successful upload
      onFileUploaded(filePath, file.name);
      
      setProgress(100);
      
      toast({
        title: "File uploaded successfully",
        description: `${file.name} has been uploaded`,
      });
      
      // Reset state
      setTimeout(() => {
        setFile(null);
        setProgress(0);
      }, 1000);
      
    } catch (error) {
      console.error("Error uploading file:", error);
      toast({
        title: "Upload failed",
        description: "There was an error uploading your file. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };
  
  const handleDelete = async () => {
    if (!currentFilePath) return;
    
    try {
      await deleteFile('course-content', currentFilePath);
      
      toast({
        title: "File deleted",
        description: "The file has been deleted successfully",
      });
      
      // Notify parent component
      onFileUploaded('', '');
      
    } catch (error) {
      console.error("Error deleting file:", error);
      toast({
        title: "Delete failed",
        description: "There was an error deleting the file. Please try again.",
        variant: "destructive",
      });
    }
  };
  
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="space-y-4">
          <div>
            <Label htmlFor="file-upload">Upload Lesson PDF</Label>
            <div className="mt-2 flex items-center gap-4">
              <Input
                id="file-upload"
                type="file"
                accept="application/pdf"
                onChange={handleFileChange}
                disabled={isUploading}
              />
              <Button 
                onClick={handleUpload} 
                disabled={!file || isUploading}
                className="whitespace-nowrap"
              >
                <Upload className="mr-2 h-4 w-4" />
                Upload
              </Button>
            </div>
            
            {isUploading && (
              <div className="mt-4">
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-techne-purple transition-all duration-300 ease-out"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  {progress < 100 ? 'Uploading...' : 'Processing...'}
                </p>
              </div>
            )}
          </div>
          
          {currentFilePath && currentFileName && (
            <div className="flex items-center justify-between p-3 border rounded-md">
              <div className="flex items-center gap-3">
                <File className="h-5 w-5 text-techne-purple" />
                <span>{currentFileName}</span>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleDelete}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          )}
          
          <div className="text-sm text-gray-500 mt-2">
            <p>* Upload PDF files for lesson content.</p>
            <p>* Maximum file size: 5MB.</p>
            <p>* Uploading a new file will replace the existing one.</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default FileUploadForm;
